// /leads endpoints (plan §4, spec F11 and §5.7): one table per record type, a record's detail, owner
// edits and pack-value review (leads.edit), and decisions on asks and acceptances (leads.decide).
// Every write carries the record version as If-Match (constitution §4.7); creates (decisions,
// readings) also carry an Idempotency-Key, so a retry on a slow phone records one (spec §8.8).
import type { z } from 'zod';
import { createHttp, type HttpOptions } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  LEADS_DETAIL_SCHEMAS,
  LEADS_PAGE_SCHEMAS,
  type AcceptanceDecision,
  type AskDecision,
  type LeadsDetailMap,
  type LeadsKind,
  type LeadsPageMap,
} from './schemas/leads.ts';

export * from './schemas/leads.ts';
export * from './schemas/leads-forms.ts';

export interface LeadsListParams {
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly status?: string | undefined;
}

export interface LeadsWrite {
  readonly kind: LeadsKind;
  readonly key: string;
  /** The version the person saw (If-Match). */
  readonly version: number;
}

export interface DecisionInput {
  readonly decision: AskDecision | AcceptanceDecision;
  readonly note?: string | undefined;
}

export interface ReadingInput {
  readonly readOn: string;
  readonly value: string;
  readonly note?: string | undefined;
}

export interface LeadsApi {
  /** GET /leads/:type: one page of records, ordered by key. */
  list<K extends LeadsKind>(
    kind: K,
    params: LeadsListParams,
    signal?: AbortSignal,
  ): Promise<LeadsPageMap[K]>;
  /** GET /leads/:type/:key: the record, its decisions or readings, and (owner) the pack review. */
  get<K extends LeadsKind>(kind: K, key: string, signal?: AbortSignal): Promise<LeadsDetailMap[K]>;
  /** PATCH /leads/:type/:key (owner): only the changed fields. */
  edit<K extends LeadsKind>(
    target: LeadsWrite & { readonly kind: K },
    changes: Readonly<Record<string, unknown>>,
  ): Promise<LeadsDetailMap[K]>;
  /** POST /leads/:type/:key/pack-value (owner): takes the pack's value for one field. */
  acceptPackValue<K extends LeadsKind>(
    target: LeadsWrite & { readonly kind: K },
    field: string,
  ): Promise<LeadsDetailMap[K]>;
  /** POST /leads/asks|acceptances/:key/decisions (lead, owner): appends a decision. */
  decide<K extends 'asks' | 'acceptances'>(
    target: LeadsWrite & { readonly kind: K },
    input: DecisionInput,
    idempotencyKey: string,
  ): Promise<LeadsDetailMap[K]>;
  /** POST /leads/kpis/:key/readings (owner): appends a reading. */
  addReading(
    key: string,
    input: ReadingInput,
    idempotencyKey: string,
  ): Promise<LeadsDetailMap['kpis']>;
}

function listQuery(params: LeadsListParams): string {
  const search = new URLSearchParams();
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const status = params.status?.trim() ?? '';
  if (status !== '') search.set('status', status);
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** The body of a decision or reading: an empty note is left out (the API stores none). */
function withNote(input: DecisionInput | ReadingInput): Record<string, string> {
  const note = input.note?.trim() ?? '';
  const body: Record<string, string> =
    'decision' in input
      ? { decision: input.decision }
      : { readOn: input.readOn, value: input.value };
  return note === '' ? body : { ...body, note };
}

// The schema tables are keyed by kind; TypeScript cannot follow a generic index into them.
const pageSchema = <K extends LeadsKind>(kind: K) =>
  LEADS_PAGE_SCHEMAS[kind] as unknown as z.ZodType<LeadsPageMap[K]>;
const detailSchema = <K extends LeadsKind>(kind: K) =>
  LEADS_DETAIL_SCHEMAS[kind] as unknown as z.ZodType<LeadsDetailMap[K]>;

export function createLeadsApi(options: HttpOptions = {}): LeadsApi {
  const http = createHttp(options);
  const recordPath = (kind: LeadsKind, key: string): string =>
    `/leads/${pathSegment(kind)}/${pathSegment(key)}`;
  // Methods are async so a refused key is a rejected promise, never a synchronous throw.
  return {
    list: async (kind, params, signal) =>
      http.json(
        { method: 'GET', path: `/leads/${pathSegment(kind)}${listQuery(params)}`, signal },
        pageSchema(kind),
      ),
    get: async (kind, key, signal) =>
      http.json({ method: 'GET', path: recordPath(kind, key), signal }, detailSchema(kind)),
    edit: async (target, changes) =>
      http.json(
        {
          method: 'PATCH',
          path: recordPath(target.kind, target.key),
          version: target.version,
          body: changes,
        },
        detailSchema(target.kind),
      ),
    acceptPackValue: async (target, field) =>
      http.json(
        {
          method: 'POST',
          path: `${recordPath(target.kind, target.key)}/pack-value`,
          version: target.version,
          body: { field },
        },
        detailSchema(target.kind),
      ),
    decide: async (target, input, idempotencyKey) =>
      http.json(
        {
          method: 'POST',
          path: `${recordPath(target.kind, target.key)}/decisions`,
          version: target.version,
          body: withNote(input),
          idempotencyKey,
        },
        detailSchema(target.kind),
      ),
    addReading: async (key, input, idempotencyKey) =>
      http.json(
        {
          method: 'POST',
          path: `${recordPath('kpis', key)}/readings`,
          body: withNote(input),
          idempotencyKey,
        },
        detailSchema('kpis'),
      ),
  };
}
